import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  ClientSchema,
  CrmListsSchema,
  EmployeeSchema,
  ProjectSchema,
  type ClientDto,
  type ClientLifecycleStage,
  type CrmListValueDto,
  type EmployeeDto,
  type ProjectDto,
} from '@taqon/contracts';
import { getData } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { PersonName } from '../../shared/ui/person';
import { RecordLink } from '../finance/detail';
import { SearchPicker } from '../finance/lookups';

/**
 * Pieces the Client, Activity and Project pages share: the CRM list values, the lifecycle stage
 * vocabulary, a confirmation for consequential actions, the pager, server-searched pickers and the
 * account-owner / employee name that links only where the session may open the employee.
 */

export const CLIENTS = '/api/v1/crm/clients';
export const ACTIVITIES = '/api/v1/crm/activities';
export const PROJECTS = '/api/v1/projects';

/** The BRD §11.5 stages, as people read them (FRD-CRM-013). */
export const STAGE_LABELS: Readonly<Record<ClientLifecycleStage, string>> = {
  lead: 'Lead',
  prospect: 'Prospect',
  qualified: 'Qualified',
  opportunity: 'Opportunity',
  client: 'Client',
  active_account: 'Active account',
  renewal_expansion: 'Renewal / expansion',
  inactive: 'Inactive',
};

/** The stages Release 1 can record: `opportunity` arrives with opportunity management (Ph3). */
export const RELEASE_ONE_STAGES: readonly ClientLifecycleStage[] = [
  'lead',
  'prospect',
  'qualified',
  'client',
  'active_account',
  'renewal_expansion',
  'inactive',
];

/** The CRM list values (industry, client type, source, activity type), inactive ones included. */
export function useCrmLists() {
  const session = useSession();
  return useQuery({
    queryKey: ['crm', 'lists'],
    enabled: hasCapability(session.data, 'crm.client:view'),
    queryFn: () => getData('/api/v1/crm/lists', CrmListsSchema),
  });
}

/** A stored list code by its label; an unknown code is shown as it is. */
export function listLabel(values: readonly CrmListValueDto[] | undefined, code: string | null) {
  if (code === null) return '—';
  const value = values?.find((v) => v.valueCode === code);
  if (value === undefined) return code;
  return value.active ? value.label : `${value.label} (inactive)`;
}

/**
 * A select over one CRM list: its active values, plus the record's current value even when it has
 * since been deactivated, so an edit never silently drops it.
 */
export function ListSelect({
  label,
  values,
  value,
  current,
  onChange,
  required,
  emptyLabel = 'None',
}: {
  label: string;
  values: readonly CrmListValueDto[] | undefined;
  value: string;
  current?: string | null | undefined;
  onChange: (value: string) => void;
  required?: boolean | undefined;
  emptyLabel?: string | undefined;
}) {
  const options = (values ?? []).filter((v) => v.active || v.valueCode === current);
  const empty = (values ?? []).filter((v) => v.active).length === 0;
  return (
    <TextField
      select
      label={label}
      value={value}
      required={required}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      helperText={empty ? 'No values are configured for this list yet.' : undefined}
    >
      {required !== true && <MenuItem value="">{emptyLabel}</MenuItem>}
      {options.map((option) => (
        <MenuItem key={option.valueCode} value={option.valueCode}>
          {option.label}
          {option.active ? '' : ' (inactive)'}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** A consequential action asks first (deactivate, complete, make primary). */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <FormDialog
      open
      title={title}
      submitLabel={confirmLabel}
      canSubmit
      submitting={pending}
      error={error}
      onClose={onClose}
      onSubmit={onConfirm}
    >
      <Typography variant="body2">{message}</Typography>
    </FormDialog>
  );
}

/** Previous / next over a server-side paged list. */
export function Pager({
  page,
  pageSize,
  total,
  noun,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  noun: string;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ p: 2, alignItems: 'center', justifyContent: 'flex-end' }}
    >
      <Typography variant="body2" color="text.secondary">
        Page {page} of {lastPage} · {total} {noun}
      </Typography>
      <Button
        size="small"
        disabled={page <= 1}
        onClick={() => {
          onPage(page - 1);
        }}
      >
        Previous
      </Button>
      <Button
        size="small"
        disabled={page >= lastPage}
        onClick={() => {
          onPage(page + 1);
        }}
      >
        Next
      </Button>
    </Stack>
  );
}

const describeEmployee = (e: EmployeeDto) => `${e.code} — ${e.name}`;
const describeClient = (c: ClientDto) => `${c.code} — ${c.companyName}`;
const describeProject = (p: ProjectDto) => `${p.code} — ${p.name}`;
const search = (key: string, term: string) =>
  term === '' ? '' : `&filter[${key}]=${encodeURIComponent(term)}`;

/** Active employees by name, searched on the server (FRD-CRM-011, FRD-HRP-010). */
export function ActiveEmployeePicker(props: {
  label: string;
  value: EmployeeDto | null;
  onChange: (value: EmployeeDto | null) => void;
  required?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      queryKey="crm-employees-active"
      schema={EmployeeSchema}
      path={(term) =>
        `/api/v1/hr/employees?pageSize=25&sort=name&filter[status]=active${search('name', term)}`
      }
      describe={describeEmployee}
      {...props}
    />
  );
}

/** Active clients by Client ID or company name, searched on the server (FRD-CRM-012). */
export function ActiveClientPicker(props: {
  label: string;
  value: ClientDto | null;
  onChange: (value: ClientDto | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      queryKey="crm-clients-active"
      schema={ClientSchema}
      path={(term) =>
        `${CLIENTS}?pageSize=25&sort=company_name&filter[status]=active${search('search', term)}`
      }
      describe={describeClient}
      {...props}
    />
  );
}

/** Active projects by code or name, searched on the server (FRD-CORE-007). */
export function ActiveProjectPicker(props: {
  label: string;
  value: ProjectDto | null;
  onChange: (value: ProjectDto | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      queryKey="projects-active"
      schema={ProjectSchema}
      path={(term) =>
        `${PROJECTS}?pageSize=25&sort=code&filter[status]=active${search('search', term)}`
      }
      describe={describeProject}
      {...props}
    />
  );
}

/**
 * Projects of any status by code or name, searched on the server: for filtering by a project a
 * record already names, where an inactive one still counts.
 */
export function ProjectSearchPicker(props: {
  label: string;
  value: ProjectDto | null;
  onChange: (value: ProjectDto | null) => void;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      queryKey="projects-any"
      schema={ProjectSchema}
      path={(term) => `${PROJECTS}?pageSize=25&sort=code${search('search', term)}`}
      describe={describeProject}
      {...props}
    />
  );
}

/** One project read on its own, for a session that may view projects; null otherwise. */
export function useProject(id: string | null) {
  const session = useSession();
  return useQuery({
    queryKey: ['projects', 'one', id],
    enabled: id !== null && hasCapability(session.data, 'projects.project:view'),
    retry: false,
    queryFn: () => getData(`${PROJECTS}/${id ?? ''}`, ProjectSchema),
  });
}

/**
 * A project a record names only by its id (an asset's placement, a custodian, a role assignment's
 * scope): read through the projects API, so it is named and linked (`/projects/:id`) only for a
 * session that may open projects.
 */
export function ProjectName({ id }: { id: string | null }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'projects.project:view');
  const project = useProject(id);
  if (id === null) return <>—</>;
  if (!canView) return <>Not visible</>;
  if (project.isPending) return <>…</>;
  if (project.data === undefined) return <>Not visible</>;
  return (
    <RecordLink to={`/projects/${id}`}>
      {project.data.code} — {project.data.name}
    </RecordLink>
  );
}

/**
 * An employee named on a CRM record (account owner, responsible employee). It is read through the
 * HR API, so it is named and linked only for a session that may open employees.
 */
export function EmployeeName({ id }: { id: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'hr.employee:view');
  const employee = useQuery({
    queryKey: ['hr', 'employee', id],
    enabled: canView,
    retry: false,
    queryFn: () => getData(`/api/v1/hr/employees/${id}`, EmployeeSchema),
  });
  if (!canView) return <>Not visible</>;
  if (employee.isPending) return <>…</>;
  if (employee.data === undefined) return <>Not visible</>;
  return (
    <PersonName name={employee.data.name} employeeId={id}>
      <RecordLink to={`/hr/employees/${id}`}>
        {employee.data.code} — {employee.data.name}
      </RecordLink>
    </PersonName>
  );
}

/**
 * A client a record names only by its id (a role assignment's client scope): read through the CRM
 * API, so it is named and linked only for a session that may open that client.
 */
export function ClientName({ id }: { id: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'crm.client:view');
  const client = useQuery({
    queryKey: ['crm', 'client', id],
    enabled: canView,
    retry: false,
    queryFn: () => getData(`${CLIENTS}/${id}`, ClientSchema),
  });
  if (!canView) return <>Not visible</>;
  if (client.isPending) return <>…</>;
  if (client.data === undefined) return <>Not visible</>;
  return (
    <RecordLink to={`/crm/clients/${id}`}>
      {client.data.code} — {client.data.companyName}
    </RecordLink>
  );
}

/** The client of a record, linked to its page for a session that may view clients. */
export function ClientRef({ id, label }: { id: string; label: string }) {
  const session = useSession();
  return hasCapability(session.data, 'crm.client:view') ? (
    <RecordLink to={`/crm/clients/${id}`}>{label}</RecordLink>
  ) : (
    <>{label}</>
  );
}

/** The project of a record, linked to its page for a session that may view projects. */
export function ProjectRef({ id, label }: { id: string | null; label: string | null }) {
  const session = useSession();
  if (id === null) return <>—</>;
  const text = label ?? 'Not visible';
  return hasCapability(session.data, 'projects.project:view') ? (
    <RecordLink to={`/projects/${id}`}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}

/** A value the masking service replaced (SDD §10.3), as the employee pages read it. */
export { isMasked } from '../hr/EmployeesPage';
